import React, { useState, useEffect } from 'react';
import { workstreamAPI, teamDirectoryAPI } from '../services/api';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { 
  Plus, ChevronRight, ChevronDown, Clock, Calendar, User,
  X, Loader2, AlertTriangle, Target, CheckCircle2, Layers,
  Lightbulb, Sparkles, TrendingUp, FolderKanban
} from 'lucide-react';

// Status configuration
const statusConfig = {
  'on-track': { bg: '#D1FAE5', text: '#059669', label: 'On Track', icon: TrendingUp },
  'focus-required': { bg: '#FEF3C7', text: '#92400E', label: 'Focus Required', icon: AlertTriangle },
  'at-risk': { bg: '#FEE2E2', text: '#DC2626', label: 'At Risk', icon: AlertTriangle }
};

// Workstream Status Badge
const WorkstreamStatusBadge = ({ status }) => {
  const config = statusConfig[status] || statusConfig['on-track'];
  const Icon = config.icon;
  
  return (
    <span 
      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full"
      style={{ backgroundColor: config.bg, color: config.text }}
    >
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
};

// Single Workstream Card
const WorkstreamCard = ({ workstream, onSelect, onDelete, isExpanded, onToggle }) => {
  const [loading, setLoading] = useState(false);
  
  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete workstream "${workstream.title}"? Tasks will be unlinked but not deleted.`)) return;
    
    setLoading(true);
    try {
      await workstreamAPI.deleteWorkstream(workstream.id);
      onDelete();
    } catch (error) {
      console.error('Error deleting workstream:', error);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div 
      className="bg-white rounded-lg border transition-all hover:shadow-md cursor-pointer"
      style={{ borderColor: '#E9ECEF' }}
      data-testid={`workstream-card-${workstream.id}`}
    >
      {/* Main Header - Clickable */}
      <div 
        className="p-4"
        onClick={() => onSelect(workstream)}
      >
        <div className="flex items-start justify-between gap-3">
          {/* Left side - Title and Meta */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <FolderKanban className="w-4 h-4 flex-shrink-0" style={{ color: '#6366F1' }} />
              <h4 className="text-sm font-semibold truncate" style={{ color: '#264653' }}>
                {workstream.title}
              </h4>
            </div>
            
            {workstream.description && (
              <p className="text-xs mt-1 line-clamp-2" style={{ color: '#6B7280' }}>
                {workstream.description}
              </p>
            )}
            
            {/* Meta info row */}
            <div className="flex items-center gap-4 mt-2">
              {workstream.owner_name && (
                <div className="flex items-center gap-1 text-xs" style={{ color: '#6B7280' }}>
                  <div 
                    className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-medium"
                    style={{ backgroundColor: '#E8F4F2', color: '#2A9D8F' }}
                  >
                    {workstream.owner_avatar || '?'}
                  </div>
                  <span>{workstream.owner_name}</span>
                </div>
              )}
              
              {workstream.due_date && (
                <div className="flex items-center gap-1 text-xs" style={{ color: '#6B7280' }}>
                  <Calendar className="w-3 h-3" />
                  <span>
                    {new Date(workstream.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                </div>
              )}
            </div>
          </div>
          
          {/* Right side - Status & Actions */}
          <div className="flex flex-col items-end gap-2">
            <WorkstreamStatusBadge status={workstream.status} />
            
            {/* Health Score */}
            {workstream.health_score !== null && (
              <div 
                className="text-xs px-2 py-1 rounded"
                style={{ 
                  backgroundColor: workstream.health_score >= 70 ? '#ECFDF5' : workstream.health_score >= 40 ? '#FEF3C7' : '#FEE2E2',
                  color: workstream.health_score >= 70 ? '#059669' : workstream.health_score >= 40 ? '#92400E' : '#DC2626'
                }}
              >
                Health: {workstream.health_score}
              </div>
            )}
          </div>
        </div>
        
        {/* Progress Section */}
        <div className="mt-3 pt-3 border-t" style={{ borderColor: '#E9ECEF' }}>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs" style={{ color: '#6B7280' }}>
              {workstream.completed_task_count}/{workstream.task_count} tasks
            </span>
            <span className="text-xs font-medium" style={{ color: '#264653' }}>
              {workstream.progress_percent}%
            </span>
          </div>
          <Progress value={workstream.progress_percent} className="h-1.5" />
          
          {/* Warnings */}
          <div className="flex items-center gap-3 mt-2">
            {workstream.overdue_task_count > 0 && (
              <span className="flex items-center gap-1 text-xs" style={{ color: '#DC2626' }}>
                <AlertTriangle className="w-3 h-3" />
                {workstream.overdue_task_count} overdue
              </span>
            )}
            {workstream.has_overdue_phases && (
              <span className="flex items-center gap-1 text-xs" style={{ color: '#F59E0B' }}>
                <Clock className="w-3 h-3" />
                Overdue phases
              </span>
            )}
          </div>
        </div>
      </div>
      
      {/* Delete Button (on hover) */}
      <div className="px-4 pb-3 flex justify-end">
        <button
          onClick={handleDelete}
          disabled={loading}
          className="text-xs px-2 py-1 rounded hover:bg-red-50 transition-colors"
          style={{ color: '#DC2626' }}
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Delete'}
        </button>
      </div>
    </div>
  );
};

// Workstream Suggestion Card
const SuggestionCard = ({ suggestion, onAccept }) => {
  const [loading, setLoading] = useState(false);
  
  const handleAccept = async () => {
    setLoading(true);
    try {
      await onAccept(suggestion);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div 
      className="p-3 rounded-lg"
      style={{ backgroundColor: '#EEF2FF', border: '1px dashed #A5B4FC' }}
    >
      <div className="flex items-start gap-2">
        <Sparkles className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#6366F1' }} />
        <div className="flex-1">
          <h5 className="text-sm font-medium" style={{ color: '#4338CA' }}>
            {suggestion.suggested_title}
          </h5>
          <p className="text-xs mt-1" style={{ color: '#6B7280' }}>
            {suggestion.reason}
          </p>
          <p className="text-xs mt-1" style={{ color: '#9CA3AF' }}>
            Includes: {suggestion.task_titles.slice(0, 3).join(', ')}
            {suggestion.task_titles.length > 3 && ` +${suggestion.task_titles.length - 3} more`}
          </p>
          <Button
            size="sm"
            onClick={handleAccept}
            disabled={loading}
            className="mt-2 h-7 text-xs"
            style={{ backgroundColor: '#6366F1' }}
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Plus className="w-3 h-3 mr-1" />}
            Create Workstream
          </Button>
        </div>
      </div>
    </div>
  );
};

// Add Workstream Form
const AddWorkstreamForm = ({ projectId, onAdd, onCancel, teamMembers = [] }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [loading, setLoading] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    
    setLoading(true);
    try {
      await workstreamAPI.createWorkstream(projectId, {
        title: title.trim(),
        description: description.trim() || null,
        owner_id: ownerId || null,
        due_date: dueDate || null
      });
      onAdd();
    } catch (error) {
      console.error('Error creating workstream:', error);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <form 
      onSubmit={handleSubmit}
      className="p-4 rounded-lg bg-white"
      style={{ border: '2px solid #2A9D8F' }}
    >
      <h4 className="text-sm font-medium mb-3" style={{ color: '#264653' }}>New Workstream</h4>
      
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Workstream title..."
        className="w-full text-sm px-3 py-2 rounded border"
        style={{ borderColor: '#E9ECEF', color: '#264653' }}
        autoFocus
      />
      
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Description (optional)..."
        className="w-full text-sm px-3 py-2 rounded border mt-2 resize-none"
        style={{ borderColor: '#E9ECEF', color: '#264653' }}
        rows={2}
      />
      
      <div className="grid grid-cols-2 gap-2 mt-2">
        <select
          value={ownerId}
          onChange={(e) => setOwnerId(e.target.value)}
          className="text-sm px-3 py-2 rounded border"
          style={{ borderColor: '#E9ECEF', color: '#6B7280' }}
        >
          <option value="">Select owner...</option>
          {teamMembers.map(member => (
            <option key={member.id} value={member.id}>
              {member.name || member.displayName}
            </option>
          ))}
        </select>
        
        <input
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          className="text-sm px-3 py-2 rounded border"
          style={{ borderColor: '#E9ECEF', color: '#6B7280' }}
        />
      </div>
      
      <div className="flex items-center justify-end gap-2 mt-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onCancel}
          className="h-8"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          size="sm"
          disabled={!title.trim() || loading}
          className="h-8"
          style={{ backgroundColor: '#2A9D8F' }}
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Plus className="w-3 h-3 mr-1" />}
          Create
        </Button>
      </div>
    </form>
  );
};

// Main Workstreams Tab Component
const WorkstreamsTab = ({ projectId, tasks = [], teamMembers = [] }) => {
  const [workstreams, setWorkstreams] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedWorkstream, setSelectedWorkstream] = useState(null);
  
  const fetchWorkstreams = async () => {
    try {
      const [workstreamsRes, suggestionsRes] = await Promise.all([
        workstreamAPI.getProjectWorkstreams(projectId),
        workstreamAPI.getSuggestions(projectId).catch(() => ({ data: { suggestions: [] } }))
      ]);
      setWorkstreams(workstreamsRes.data.workstreams || []);
      setSuggestions(suggestionsRes.data.suggestions || []);
    } catch (error) {
      console.error('Error fetching workstreams:', error);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (projectId) {
      fetchWorkstreams();
    }
  }, [projectId]);
  
  const handleAcceptSuggestion = async (suggestion) => {
    try {
      // Create workstream from suggestion
      const res = await workstreamAPI.createWorkstream(projectId, {
        title: suggestion.suggested_title,
        description: `Auto-created from ${suggestion.task_ids.length} tasks in ${suggestion.common_lane} lane`
      });
      
      // Assign tasks to the new workstream
      const workstreamId = res.data.workstream.id;
      for (const taskId of suggestion.task_ids) {
        await workstreamAPI.assignTask(taskId, workstreamId).catch(() => {});
      }
      
      fetchWorkstreams();
    } catch (error) {
      console.error('Error creating workstream from suggestion:', error);
    }
  };
  
  // Calculate summary stats
  const totalTasks = workstreams.reduce((acc, w) => acc + w.task_count, 0);
  const completedTasks = workstreams.reduce((acc, w) => acc + w.completed_task_count, 0);
  const atRiskCount = workstreams.filter(w => w.status === 'at-risk').length;
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#2A9D8F' }} />
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="workstreams-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold" style={{ color: '#264653' }}>
            Workstreams
          </h3>
          <p className="text-sm" style={{ color: '#6B7280' }}>
            Group and track related tasks together
          </p>
        </div>
        <Button 
          onClick={() => setShowAddForm(true)}
          className="gap-2"
          style={{ backgroundColor: '#2A9D8F' }}
        >
          <Plus className="w-4 h-4" />
          New Workstream
        </Button>
      </div>
      
      {/* Summary Stats */}
      {workstreams.length > 0 && (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#E9ECEF' }}>
            <p className="text-2xl font-semibold" style={{ color: '#264653' }}>{workstreams.length}</p>
            <p className="text-xs" style={{ color: '#6B7280' }}>Workstreams</p>
          </div>
          <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#E9ECEF' }}>
            <p className="text-2xl font-semibold" style={{ color: '#2A9D8F' }}>{completedTasks}</p>
            <p className="text-xs" style={{ color: '#6B7280' }}>Completed Tasks</p>
          </div>
          <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#E9ECEF' }}>
            <p className="text-2xl font-semibold" style={{ color: '#3B82F6' }}>{totalTasks - completedTasks}</p>
            <p className="text-xs" style={{ color: '#6B7280' }}>Remaining</p>
          </div>
          <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#E9ECEF' }}>
            <p className="text-2xl font-semibold" style={{ color: atRiskCount > 0 ? '#DC2626' : '#059669' }}>{atRiskCount}</p>
            <p className="text-xs" style={{ color: '#6B7280' }}>At Risk</p>
          </div>
        </div>
      )}
      
      {/* Add Workstream Form */}
      {showAddForm && (
        <AddWorkstreamForm
          projectId={projectId}
          teamMembers={teamMembers}
          onAdd={() => {
            setShowAddForm(false);
            fetchWorkstreams();
          }}
          onCancel={() => setShowAddForm(false)}
        />
      )}
      
      {/* Workstreams Grid */}
      {workstreams.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {workstreams.map(workstream => (
            <WorkstreamCard
              key={workstream.id}
              workstream={workstream}
              onSelect={(ws) => setSelectedWorkstream(ws)}
              onDelete={fetchWorkstreams}
            />
          ))}
        </div>
      ) : !showAddForm && (
        <div 
          className="text-center py-12 rounded-lg"
          style={{ backgroundColor: '#F9FAFB', border: '1px dashed #D1D5DB' }}
        >
          <Layers className="w-10 h-10 mx-auto mb-3" style={{ color: '#9CA3AF' }} />
          <h4 className="text-base font-medium mb-1" style={{ color: '#374151' }}>
            No workstreams yet
          </h4>
          <p className="text-sm mb-4" style={{ color: '#6B7280' }}>
            Create workstreams to organize related tasks into logical groups
          </p>
          <Button
            onClick={() => setShowAddForm(true)}
            variant="outline"
            className="gap-2"
          >
            <Plus className="w-4 h-4" />
            Create First Workstream
          </Button>
        </div>
      )}
      
      {/* AI Suggestions */}
      {suggestions.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Lightbulb className="w-4 h-4" style={{ color: '#F59E0B' }} />
            <h4 className="text-sm font-medium" style={{ color: '#264653' }}>
              Suggested Workstreams
            </h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {suggestions.map((suggestion, idx) => (
              <SuggestionCard
                key={idx}
                suggestion={suggestion}
                onAccept={handleAcceptSuggestion}
              />
            ))}
          </div>
        </div>
      )}
      
      {/* Unassigned Tasks Notice */}
      {tasks.filter(t => !t.workstream_id).length > 0 && workstreams.length > 0 && (
        <div 
          className="p-4 rounded-lg flex items-center justify-between"
          style={{ backgroundColor: '#FEF3C7', border: '1px solid #FDE68A' }}
        >
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5" style={{ color: '#92400E' }} />
            <div>
              <p className="text-sm font-medium" style={{ color: '#78350F' }}>
                {tasks.filter(t => !t.workstream_id).length} tasks not assigned to any workstream
              </p>
              <p className="text-xs" style={{ color: '#92400E' }}>
                Consider organizing them into workstreams for better tracking
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkstreamsTab;
